import * as duration from "iso8601-duration";

/**
 * Formats a date to time string in HH:MM format
 * @param date - The date to format
 * @returns Formatted time string (e.g., "14:30")
 * @example
 * formatTime(new Date("2024-01-15T14:30:00")) // "14:30"
 */
export function formatTime(date: Date): string {
    return date.toLocaleTimeString(navigator.language, {
        hour12: false,
        hour: "numeric",
        minute: "numeric",
    });
}

/**
 * Converts ISO 8601 duration string to minutes
 * @param isoDuration - ISO 8601 duration string (e.g., "PT15M", "PT1H30M")
 * @returns Duration in minutes, rounded up
 * @example
 * getDurationInMinutes("PT15M") // 15
 * getDurationInMinutes("PT1H30M") // 90
 */
export function getDurationInMinutes(isoDuration: string): number {
    const parsed = duration.parse(isoDuration);
    const durationInMinutes = Math.ceil(duration.toSeconds(parsed) / 60);
    return durationInMinutes;
}

/**
 * Calculates and formats the dropoff arrival time based on current time,
 * estimated arrival duration, and estimated ride duration
 * @param estimatedArrival - ISO 8601 duration for pickup arrival
 * @param estimatedRide - ISO 8601 duration for the ride
 * @returns Formatted time string with ~ prefix (e.g., "~15:30")
 * @example
 * formatDropoffArrival("PT10M", "PT20M") // "~15:30" (if current time is 15:00)
 */
export function formatDropoffArrival(estimatedArrival: string, estimatedRide: string): string {
    const time = new Date(
        new Date().getTime() +
            getDurationInMinutes(estimatedArrival) * 60 * 1000 +
            getDurationInMinutes(estimatedRide) * 60 * 1000
    );
    return `~${formatTime(time)}`;
}

/**
 * Formats pickup duration to human-readable string
 * @param pickupDuration - ISO 8601 duration for pickup
 * @returns Formatted duration string (e.g., "~15 min")
 * @example
 * formatPickupArrival("PT15M") // "~15 min"
 */
export function formatPickupArrival(pickupDuration: string): string {
    return `~${getDurationInMinutes(pickupDuration).toString()} min`;
}
